import { lazy, Suspense, useMemo } from 'react';

const Globe = lazy(() => import('react-globe.gl'));

/**
 * Interactive 3D globe used as the hero backdrop.
 * Lazy-loaded (react-globe.gl is heavy). Void-black background bleeds through.
 * Points + arcs give the "around the world" motion signature.
 */
export default function InteractiveGlobe({ size = 700 }) {
  const pts = useMemo(
    () => [
      { lat: 28.6, lng: 77.2, name: 'Delhi', size: 0.6, color: '#00F0FF' },
      { lat: 40.71, lng: -74.0, name: 'New York', size: 0.6, color: '#FF007F' },
      { lat: 51.5, lng: -0.12, name: 'London', size: 0.5, color: '#00F0FF' },
      { lat: 35.68, lng: 139.69, name: 'Tokyo', size: 0.7, color: '#FF007F' },
      { lat: 25.2, lng: 55.27, name: 'Dubai', size: 0.5, color: '#00F0FF' },
      { lat: -33.86, lng: 151.2, name: 'Sydney', size: 0.55, color: '#FF007F' },
      { lat: 1.29, lng: 103.85, name: 'Singapore', size: 0.5, color: '#00F0FF' },
      { lat: -23.5, lng: -46.6, name: 'São Paulo', size: 0.5, color: '#FF007F' },
      { lat: 37.77, lng: -122.42, name: 'San Francisco', size: 0.6, color: '#00F0FF' },
      { lat: 52.52, lng: 13.4, name: 'Berlin', size: 0.45, color: '#00F0FF' },
      { lat: 55.75, lng: 37.61, name: 'Moscow', size: 0.45, color: '#FF007F' },
      { lat: -1.29, lng: 36.82, name: 'Nairobi', size: 0.5, color: '#00F0FF' },
    ],
    []
  );

  const arcs = useMemo(
    () => [
      { startLat: 28.6, startLng: 77.2, endLat: 40.71, endLng: -74.0, color: ['#00F0FF', '#FF007F'] },
      { startLat: 51.5, startLng: -0.12, endLat: 35.68, endLng: 139.69, color: ['#FF007F', '#00F0FF'] },
      { startLat: 37.77, startLng: -122.42, endLat: 1.29, endLng: 103.85, color: ['#00F0FF', '#FF007F'] },
      { startLat: 25.2, startLng: 55.27, endLat: -33.86, endLng: 151.2, color: ['#FF007F', '#00F0FF'] },
      { startLat: -23.5, startLng: -46.6, endLat: 52.52, endLng: 13.4, color: ['#00F0FF', '#FF007F'] },
      { startLat: -1.29, startLng: 36.82, endLat: 55.75, endLng: 37.61, color: ['#FF007F', '#00F0FF'] },
    ],
    []
  );

  return (
    <div
      style={{ width: size, height: size }}
      className="relative"
      aria-hidden="true"
    >
      <Suspense fallback={<div />}>
        <Globe
          width={size}
          height={size}
          backgroundColor="rgba(0,0,0,0)"
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
          bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
          showAtmosphere={true}
          atmosphereColor="#00F0FF"
          atmosphereAltitude={0.18}
          pointsData={pts}
          pointLat="lat"
          pointLng="lng"
          pointColor="color"
          pointAltitude={0.02}
          pointRadius="size"
          pointResolution={12}
          arcsData={arcs}
          arcColor="color"
          arcAltitude={0.25}
          arcStroke={0.5}
          arcDashLength={0.4}
          arcDashGap={2}
          arcDashAnimateTime={3000}
          arcDashInitialGap={() => Math.random() * 3}
          enablePointerInteraction={false}
        />
      </Suspense>
    </div>
  );
}
